// Transactions table body for Bank account history.
// Column visibility, compact density, show-in-one-line, paper ledger mode, and
// rows per page all come from the BankHistorySettings passed in by Dashboard.
// Clicking any cell in a non-editing row opens inline edit mode for that row.
// Only one row at a time can be in edit mode.
// Row hover color (#EEF4FB) extends to the content card's left and right edges
// via a shadow trick on the first and last td, matching the header strip.

import React, { useState } from "react";
import { Paperclip } from "lucide-react";
import type { BankTransaction } from "../types";
import { BANK_HISTORY_COL_WIDTHS, type ColumnLayout, type BankHistorySettings } from "../constants";
import { fmtAmount, fmtDate, labelForType } from "../utils";
import EditTransactionRow from "./EditTransactionRow";

const HOVER_EDGE = "md:group-hover:first:shadow-[-32px_0_0_#EEF4FB] md:group-hover:last:shadow-[32px_0_0_#EEF4FB]";

interface TransactionsHistoryTableProps {
  rows: Array<BankTransaction & { runningBalance: number }>;
  layout: ColumnLayout;
  settings: BankHistorySettings;
  pageStart: number;
  pageEnd: number;
  total: number;
  page: number;
  pageSize: number;
  onPrev: () => void;
  onNext: () => void;
  onSaveTransaction?: (updated: BankTransaction) => void;
  onDeleteTransaction?: (id: string) => void;
  onEditTransactionFull?: (id: string) => void;
}

export default function TransactionsHistoryTable({
  rows,
  layout,
  settings,
  pageStart,
  pageEnd,
  total,
  page,
  pageSize,
  onPrev,
  onNext,
  onSaveTransaction,
  onDeleteTransaction,
  onEditTransactionFull,
}: TransactionsHistoryTableProps) {
  const [editingId, setEditingId] = useState<string | null>(null);

  if (rows.length === 0) {
    return (
      <div className="border border-line rounded-lg py-12 text-center text-ink3 text-[13px] mx-4 md:mx-8">
        No transactions match your filter.
      </div>
    );
  }

  const colW = (key: string): React.CSSProperties => {
    const w = BANK_HISTORY_COL_WIDTHS[key];
    return w == null ? {} : { width: w };
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const canPrev = page > 1;
  const canNext = page < totalPages;

  const leftOf = (t: BankTransaction): number | undefined => t[layout.leftFieldKey];
  const rightOf = (t: BankTransaction): number | undefined => t[layout.rightFieldKey];

  const padY = settings.compact ? "py-1.5" : "py-2.5";
  const paperStripe = (idx: number): string =>
    settings.paperLedgerMode ? (idx % 2 === 0 ? "bg-[#FDFCF4]" : "bg-white") : "";

  const handleSave = (updated: BankTransaction) => {
    onSaveTransaction?.(updated);
    setEditingId(null);
  };
  const handleCancel = () => setEditingId(null);
  const handleDelete = (id: string) => {
    onDeleteTransaction?.(id);
    setEditingId(null);
  };

  return (
    <>
      <div className="hidden md:block">
        <table className="w-full border-collapse text-[12.5px]" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={colW("date")} />
            <col style={colW("refType")} />
            <col style={colW("payeeAccount")} />
            {settings.columns.memo && <col style={colW("memo")} />}
            <col style={colW("payment")} />
            <col style={colW("deposit")} />
            {settings.columns.attachments && <col style={colW("attachments")} />}
            {settings.columns.reconcileStatus && <col style={colW("check")} />}
            {settings.columns.runningBalance && <col style={colW("balance")} />}
          </colgroup>
          <tbody>
            {rows.map((t, idx) => {
              if (t.id === editingId) {
                return (
                  <EditTransactionRow
                    key={t.id}
                    original={t}
                    layout={layout}
                    settings={settings}
                    onSave={handleSave}
                    onCancel={handleCancel}
                    onDelete={() => handleDelete(t.id)}
                    onEditFull={onEditTransactionFull ? () => onEditTransactionFull(t.id) : undefined}
                  />
                );
              }
              const leftVal = leftOf(t);
              const rightVal = rightOf(t);
              return (
                <tr
                  key={t.id}
                  onClick={() => setEditingId(t.id)}
                  className={`group cursor-pointer hover:bg-[#EEF4FB] ${paperStripe(idx)}`}
                >
                  <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-ink ${HOVER_EDGE}`}>{fmtDate(t.date)}</td>
                  <td className={`px-3.5 ${padY} border-b border-lineSoft align-top ${HOVER_EDGE}`}>
                    {settings.showInOneLine ? (
                      <div className="text-ink">
                        {t.refNo}
                        {t.type && <span className="text-ink3 ml-2">· {labelForType(t.type)}</span>}
                      </div>
                    ) : (
                      <>
                        <div className="text-ink">{t.refNo}</div>
                        {t.type && <div className="text-ink3 text-[11.5px] mt-0.5">{labelForType(t.type)}</div>}
                      </>
                    )}
                  </td>
                  <td className={`px-3.5 ${padY} border-b border-lineSoft align-top ${HOVER_EDGE}`}>
                    {settings.showInOneLine ? (
                      <div className="text-ink">
                        {t.payee}
                        {t.linkedAccountName && <span className="text-ink3 ml-2">· {t.linkedAccountName}</span>}
                      </div>
                    ) : (
                      <>
                        <div className="text-ink">{t.payee}</div>
                        <div className="text-ink3 text-[11.5px] mt-0.5">{t.linkedAccountName}</div>
                      </>
                    )}
                  </td>
                  {settings.columns.memo && (
                    <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-ink2 ${HOVER_EDGE}`}>{t.memo ?? ""}</td>
                  )}
                  <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-right tabular-nums whitespace-nowrap ${HOVER_EDGE}`}>
                    {leftVal != null ? <span className="text-ink font-bold">${fmtAmount(leftVal)}</span> : ""}
                  </td>
                  <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-right tabular-nums whitespace-nowrap ${HOVER_EDGE}`}>
                    {rightVal != null ? <span className="text-ink font-bold">${fmtAmount(rightVal)}</span> : ""}
                  </td>
                  {settings.columns.attachments && (
                    <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-center ${HOVER_EDGE}`}>
                      <Paperclip size={12} className="inline-block text-ink4" aria-hidden="true" />
                    </td>
                  )}
                  {settings.columns.reconcileStatus && (
                    <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-center ${HOVER_EDGE}`}>
                      {t.reconcileStatus === "reconciled" && <span className="text-teal-dark font-extrabold">R</span>}
                      {t.reconcileStatus === "cleared" && <span className="text-teal-dark font-extrabold">C</span>}
                      {t.reconcileStatus === "unreconciled" && <span className="text-ink4">·</span>}
                    </td>
                  )}
                  {settings.columns.runningBalance && (
                    <td className={`px-3.5 ${padY} border-b border-lineSoft align-top text-right tabular-nums whitespace-nowrap ${HOVER_EDGE}`}>
                      <span className={`font-bold ${t.runningBalance < 0 ? "text-[#B33E1A]" : "text-ink"}`}>
                        {t.runningBalance < 0 ? "-$" : "$"}{fmtAmount(Math.abs(t.runningBalance))}
                      </span>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="flex items-center justify-between px-4 py-2.5 bg-panel border-t border-line text-[11.5px] text-ink3">
          <span>
            Showing <strong className="text-ink2 tabular-nums">{pageStart}-{pageEnd}</strong> of <strong className="text-ink2 tabular-nums">{total}</strong> transactions
          </span>
          <div className="inline-flex items-center gap-3">
            <span className="text-ink4">Rows per page: {pageSize}</span>
            <span className="text-ink4">·</span>
            <button type="button" onClick={onPrev} disabled={!canPrev} className="px-2 py-1 text-ink3 bg-transparent border-none cursor-pointer hover:text-ink disabled:text-ink4 disabled:cursor-not-allowed">‹ Previous</button>
            <strong className="text-ink2 tabular-nums">Page {page} of {totalPages}</strong>
            <button type="button" onClick={onNext} disabled={!canNext} className="px-2 py-1 text-ink3 bg-transparent border-none cursor-pointer hover:text-ink disabled:text-ink4 disabled:cursor-not-allowed">Next ›</button>
          </div>
        </div>
      </div>

      <div className="md:hidden">
        <div className="px-4">
          {rows.map((t) => {
            const leftVal = leftOf(t);
            const rightVal = rightOf(t);
            const primary = rightVal != null ? rightVal : leftVal;
            const isRight = rightVal != null;
            return (
              <div key={t.id} className="py-3 border-b border-lineSoft cursor-pointer" onClick={() => setEditingId(t.id)}>
                <div className="flex justify-between items-baseline gap-3 mb-0.5">
                  <span className="text-[13px] font-semibold text-ink">{t.payee}</span>
                  <span className={`text-[13.5px] font-bold tabular-nums ${isRight ? "text-teal-dark" : "text-ink"}`}>
                    {isRight ? "+$" : "-$"}{fmtAmount(primary ?? 0)}
                  </span>
                </div>
                <div className="text-[11px] text-ink3">
                  {fmtDate(t.date)} · {labelForType(t.type)} · {t.linkedAccountName}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between px-4 py-3 bg-panel border-t border-line text-[11.5px] text-ink3 mt-2">
          <span>{pageStart}-{pageEnd} of {total}</span>
          <div className="inline-flex items-center gap-2">
            <button type="button" onClick={onPrev} disabled={!canPrev} className="px-2 py-1 text-ink3 bg-transparent border-none cursor-pointer disabled:text-ink4">‹</button>
            <span className="tabular-nums">{page}/{totalPages}</span>
            <button type="button" onClick={onNext} disabled={!canNext} className="px-2 py-1 text-ink3 bg-transparent border-none cursor-pointer disabled:text-ink4">›</button>
          </div>
        </div>
      </div>
    </>
  );
}